import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  catchError,
  exhaustMap,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import type { MultiPolygon, Polygon } from 'geojson';
import { environment } from '../../../environments/environment';
import { JsonResponse } from './backend-config';
import { retryTransient } from './request-retry';

export interface CellCreateResponse {
  id: string;
  /**
   * The registered cell's own shape, echoed back.
   *
   * A MultiPolygon in practice — every caller registers a cell from a
   * MULTIPOLYGON WKT, which is what the backend stores and returns. This was
   * declared as a bare `number[][][]` (a Polygon's coordinates) until a typed
   * fixture of a real response refused to compile against it; nothing broke
   * meanwhile because the only consumers are the sales-forecast pages, which
   * hand it to turf and Mapbox GL, and both switch on `type` at runtime.
   */
  geometry: Polygon | MultiPolygon;
  createdByUser: number;
  createdAt: string;
  updatedAt: string;
  name: string;
  notes: string | null;
  geoId: string;
}

export interface CellIndexAsyncResponse {
  estimatedIndexTimeSeconds: number;
}

type CellIndexStatus = 'IN_PROGRESS' | 'READY' | 'ERROR';

export interface IndexCellOptions {
  /** Prefixes the retry and poll-failure logs, e.g. `'sales-forecast'`. */
  logLabel: string;
  /** The backend's estimate of how long indexing takes, in seconds. */
  onEstimate?: (estimatedSeconds: number) => void;
}

const INDEX_POLL_INTERVAL_MS = 2000;

// A status that never settles must not hold the caller open forever. The
// estimate grows with the area, so a large one gets proportionally longer
// than the floor before it counts as stuck.
const MIN_INDEX_TIMEOUT_MS = 3 * 60 * 1000;
const INDEX_ESTIMATE_HEADROOM = 3;

// How many status polls in a row may fail (each having already exhausted its
// own retries) before indexing is given up on. On a flaky connection a poll
// failing somewhere in a long wait is likely, and the backend keeps indexing
// whether or not we can hear it. A permanently broken endpoint still fails,
// just a few polls later.
const MAX_CONSECUTIVE_STATUS_FAILURES = 3;

/**
 * The backend's arbitrary-cell endpoints: register a polygon as a cell, then
 * index it so its features can be queried. Plain requests with no UI, shared
 * by the main map, the comparison tool and the sales-forecast pages.
 */
@Injectable({ providedIn: 'root' })
export class ArbitraryCellApiService {
  private readonly cellUrl = environment.apiUrl + 'cell';

  constructor(private http: HttpClient) {}

  /**
   * Not retried here: a replay mints an orphan cell, which only the caller
   * can decide is worth it. A 422 means the area is outside coverage.
   */
  public createCell(
    multipolygonWkt: string,
    name: string
  ): Observable<JsonResponse<CellCreateResponse>> {
    return this.http.post<JsonResponse<CellCreateResponse>>(this.cellUrl, {
      multipolygonWkt,
      name,
      notes: 'null',
    });
  }

  /**
   * Starts indexing and polls its status until READY. Errors on an ERROR
   * status, on too many failed polls in a row, and on a deadline.
   */
  public indexCellAsync(
    geoId: string,
    { logLabel, onEstimate }: IndexCellOptions
  ): Observable<void> {
    return (
      this.http
        .post<JsonResponse<CellIndexAsyncResponse>>(
          `${this.cellUrl}/index-async?cellId=${geoId}`,
          {}
        )
        // Safe to replay despite being a POST: it only asks the backend to
        // index a cell that already exists, keyed by cellId.
        .pipe(
          retryTransient(`${logLabel} cell index`),
          timeout(MIN_INDEX_TIMEOUT_MS),
          switchMap((res) => {
            const estimatedSeconds = res.object?.estimatedIndexTimeSeconds ?? 0;
            onEstimate?.(estimatedSeconds);

            return this.pollIndexStatus(geoId, logLabel).pipe(
              timeout(
                Math.max(
                  MIN_INDEX_TIMEOUT_MS,
                  estimatedSeconds * 1000 * INDEX_ESTIMATE_HEADROOM
                )
              )
            );
          })
        )
    );
  }

  private pollIndexStatus(geoId: string, logLabel: string): Observable<void> {
    // Counts a *streak*: reset only by a poll that actually answered.
    let consecutiveFailures = 0;

    return timer(0, INDEX_POLL_INTERVAL_MS).pipe(
      // exhaustMap, NOT switchMap: while a status GET is still in flight,
      // ignore the next tick instead of cancelling the request. The status
      // endpoint is slow while indexing runs, so switchMap would abort every
      // poll mid-response (server-side broken pipe) and only ever succeed on
      // the fast final "READY" call.
      exhaustMap(() =>
        this.http
          .get<
            JsonResponse<CellIndexStatus>
          >(`${this.cellUrl}/index-async/status?cellId=${geoId}`)
          .pipe(
            retryTransient(`${logLabel} index status`),
            map((res) => res.object),
            tap(() => (consecutiveFailures = 0)),
            catchError((err) => {
              consecutiveFailures++;
              if (consecutiveFailures > MAX_CONSECUTIVE_STATUS_FAILURES) {
                return throwError(() => err);
              }
              console.warn(
                `${logLabel} index status poll failed ` +
                  `(${consecutiveFailures}/${MAX_CONSECUTIVE_STATUS_FAILURES}), ` +
                  'still waiting:',
                err
              );
              return of<CellIndexStatus>('IN_PROGRESS');
            })
          )
      ),
      // Outside the catchError above on purpose: ERROR is the backend saying
      // indexing failed, not a transport problem to wait out.
      tap((status) => {
        if (status === 'ERROR') {
          throw new Error('Cell indexing failed');
        }
      }),
      filter((status) => status === 'READY'),
      take(1),
      map(() => undefined)
    );
  }
}
